'use client';

import { Button } from '@sprd/sprd-component-kit/v2';
import clsx from 'clsx';
import { useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { useAddItem } from '@/hooks/useCartHooks';
import { useIsDesktop } from '@/hooks/useMediaQuery';
import type { ProductTypeWithEmbroideryInformation } from '@/app/[abTest]/[locale]/(spreadshirt)/cyo/assortment/detail/actions/productType';
import { createProduct } from '@/app/actions/product';
import { Price } from '@/components/Price/Price';
import { basketAtom } from '@/store/atoms';
import { Locale } from '@/utility/locale';

const voucherCardColors = [
  'bg-brand-yellow-contrast',
  'bg-brand-pink-contrast',
  'bg-brand-blue-contrast',
  'bg-brand-green-contrast',
];

export default function GiftVoucherCard(props: {
  productType: ProductTypeWithEmbroideryInformation;
  locale: Locale;
  backgroundColor?: string;
  title?: string;
  colorIndex: number;
  shopId: string;
  createProduct: typeof createProduct;
}) {
  const { productType, backgroundColor, title, colorIndex, shopId, createProduct } = props;
  const t = useTranslations();
  const isDesktop = useIsDesktop();
  const addItem = useAddItem('GiftVoucherModule');
  const setOpenBasket = useSetAtom(basketAtom);

  let cardStyle = {};
  let cardClass = voucherCardColors[0];
  if (backgroundColor) {
    cardStyle = { backgroundColor: backgroundColor };
  } else {
    cardClass = voucherCardColors[colorIndex % voucherCardColors.length];
  }

  const addToBasket = async () => {
    const size = productType.sizes[0]?.id;
    const appearanceId = productType.appearances[0]?.id;
    const defaultViewId = productType.defaultValues?.defaultView?.id || '1';
    if (!size || !appearanceId || !defaultViewId) {
      console.log('Missing required product details for adding to basket.');
      return;
    }

    try {
      const product = await createProduct({
        shopId,
        productTypeId: productType.id,
        appearanceId,
        defaultViewId,
      });
      addItem.mutate(
        {
          cartItems: [
            {
              productId: Number(product.id),
              productTypeId: Number(productType.id),
              shopId: Number(shopId),
              appearanceId: appearanceId,
              quantity: 1,
              price: productType.price.vatIncluded,
              sizeId: size,
              editLink: window.location.href,
            },
          ],
        },
        {
          onSuccess: () => {
            setOpenBasket(true);
          },
        }
      );
    } catch (error) {
      console.log('Failed to add gift voucher to basket:', error);
    }
  };

  return (
    <div className={clsx(cardClass, 'flex flex-col gap-4 p-4 md:p-6')} style={cardStyle}>
      <div className="min-h-18 justify-start self-stretch font-made text-lg leading-6 wrap-break-word sm:min-h-0 md:min-h-20 md:text-2xl md:leading-8">
        {title || productType.name}
      </div>
      <div className="mt-auto flex flex-col justify-start">
        <Price
          format="default"
          className="font-made text-xl leading-7 font-black uppercase md:text-3xl md:leading-9"
        >
          {productType.price.vatIncluded}
        </Price>
        <span className="mt-1 text-xs">{t('giftVoucher.vatIncluded')}</span>
        <Button
          onClick={() => addToBasket()}
          variant="primary"
          size={isDesktop ? 'l' : 's'}
          className="mt-4 w-full"
        >
          {t('mpDetailPage.addToCart')}
        </Button>
      </div>
    </div>
  );
}
